import { useEffect } from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { useMaintenance } from './hooks/useMaintenance';

import ConfirmationProvider from './contexts/ConfirmationContext';
import DataProvider from './contexts/DataContext';
import FiltersProvider from './contexts/FiltersContext';
import ModalProvider from './contexts/ModalContext';
import TranslationsProvider from './contexts/TranslationsContext';

import { getLanguage } from './functions/localStorage';

import PageError404 from './pages/Error404';
import PageHome from './pages/Home';
import PageLegal from './pages/Legal';
import PageMaintenance from './pages/Maintenance';
import PageChangePassword from './pages/PageChangePassword';
import PagePatientForm from './pages/PatientForm';
import PagePatientList from './pages/PatientList';
import PagePatientReport from './pages/PatientReport';
import PageUserForm from './pages/UserForm';
import PageUserList from './pages/UserList';

import MaintenanceBanner from './components/MaintenanceBanner';
import Modals from './components/Modals';
import WindowEvents from './components/WindowEvents';

import { ToastContainer } from 'react-toastify';
import i18n from './i18n';
import 'react-toastify/dist/ReactToastify.css';

const App = () => {
    const { maintenance } = useMaintenance();

    // Change language if the user has changed it before.
    useEffect(() => {
        const savedLanguage = getLanguage();
        if (savedLanguage) {
            i18n.changeLanguage(savedLanguage);
        }
    }, []);

    return (
        <DataProvider>
            <TranslationsProvider>
                <FiltersProvider>
                    <ModalProvider>
                        <ConfirmationProvider>
                            <Router
                            // future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
                            >
                                <Modals />
                                <MaintenanceBanner data={maintenance} />
                                {maintenance.isActive ? <PageMaintenance /> : <Pages />}
                                {/* Toast container global para notificaciones*/}
                                <ToastContainer
                                    position="top-right"
                                    autoClose={5000}
                                    hideProgressBar={false}
                                    newestOnTop={false}
                                    closeOnClick
                                    pauseOnHover
                                    draggable
                                    pauseOnFocusLoss
                                    closeButton={false}
                                />
                                <WindowEvents />
                            </Router>
                        </ConfirmationProvider>
                    </ModalProvider>
                </FiltersProvider>
            </TranslationsProvider>
        </DataProvider>
    );
};

const Pages = () => {
    return (
        <Routes>
            <Route
                path="/"
                element={<PageHome />}
            />
            <Route
                path="/changepassword"
                element={<PageChangePassword />}
            />
            <Route
                path="/legal"
                element={<PageLegal />}
            />
            <Route
                path="/patient/add"
                element={<PagePatientForm />}
            />
            <Route
                path="/patient/list"
                element={<PagePatientList />}
            />
            <Route
                path="/patient/report/:id"
                element={<PagePatientReport />}
            />
            <Route
                path="/patient/:id"
                element={<PagePatientForm />}
            />
            <Route
                path="/user/add"
                element={<PageUserForm />}
            />
            <Route
                path="/user/list"
                element={<PageUserList />}
            />
            <Route
                path="/user/:id"
                element={<PageUserForm />}
            />
            <Route
                path="*"
                element={<PageError404 />}
            />
        </Routes>
    );
};

export default App;

// Note about maintenance:
// -------------------------------------------------------------------------------------------------
// When we want to put the app in maintenance mode, we need to edit the maintenance.json file in two ways:
// 1. Some days before the maintenance:
//    1.1 Set isScheduled to true.
//    1.2 Set the three messages in the three languages to the message we want to show to the users.
// 2. When the maintenance starts:
//    2.1 Set isActive to true.
// 3. When the maintenance ends:
//    3.1 Set isActive to false.
//    3.2 Set isScheduled to false.

// nginx:
// -------------------------------------------------------------------------------------------------
// location /maintenance.json {
//     add_header Cache-Control "no-cache, no-store, must-revalidate";
//     add_header Pragma no-cache;
//     add_header Expires 0;
// }
